import { Button, Flex, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconKeyboard, IconMicrophone } from "@tabler/icons-react";
import { useCallback, useEffect, useMemo, useReducer } from "react";

import { useAudioLevel } from "../../hooks/useAudioLevel";
import { useMediaDevices } from "../../hooks/useMediaDevices";
import {
  MicrophonePermissionStatus,
  useMicrophone,
} from "../../hooks/useMicrophone";
import type { VoiceUrls } from "../../types";
import { translateIdentity, type StaticTextTranslator } from "../../utils";

import { AudioLevelIndicator } from "./AudioLevelIndicator";
import { DeviceSelector } from "./DeviceSelector";
import { Playback } from "./Playback";
import { VOICE_TEST_IDS } from "./voice-test-ids";
import {
  VoiceAction,
  VoiceRecordingState,
  createInitialVoiceState,
  voiceReducer,
} from "./voice-state-machine";

import styles from "./Voice.module.css";

const DEFAULT_MAX_DURATION_SECONDS = 60;

const formatCountdown = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

interface VoiceProps {
  /** Whether the question allows skipping — affects upload-failure messaging. */
  canSkip?: boolean;
  /** Called with the S3 audio key once the recording has uploaded successfully. */
  onSubmit: (audioKey: string) => void | Promise<void>;
  /** Notifies the parent when an upload is in flight so it can disable
   * sibling controls (e.g. Skip) that would otherwise race with submit. */
  onUploadingChange?: (uploading: boolean) => void;
  maxDurationSeconds?: number;
  onReset: () => void;
  onSwitchToText?: () => void;
  resetCount: number;
  translate?: StaticTextTranslator;
  voiceUrls: VoiceUrls;
}

export const Voice = ({
  canSkip,
  onSubmit,
  onUploadingChange,
  maxDurationSeconds = DEFAULT_MAX_DURATION_SECONDS,
  onReset,
  onSwitchToText,
  resetCount,
  translate = translateIdentity,
  voiceUrls,
}: VoiceProps) => {
  const [state, dispatch] = useReducer(
    voiceReducer,
    resetCount,
    createInitialVoiceState,
  );

  // Bridge the microphone hook's permission callbacks to the state machine.
  const setPermissionState = useCallback(
    (
      statusOrUpdater:
        | MicrophonePermissionStatus
        | ((prev: MicrophonePermissionStatus) => MicrophonePermissionStatus),
    ) => {
      const status =
        typeof statusOrUpdater === "function"
          ? statusOrUpdater(state.permissionStatus)
          : statusOrUpdater;

      if (status === MicrophonePermissionStatus.Granted) {
        dispatch({ type: VoiceAction.PermissionGranted });
      } else if (status === MicrophonePermissionStatus.Denied) {
        dispatch({ type: VoiceAction.PermissionDenied });
      }
    },
    [state.permissionStatus],
  );

  const {
    duration,
    isRecording,
    requestPermissions,
    reset,
    startRecording,
    stopRecording,
    stream,
  } = useMicrophone(onReset, setPermissionState);

  const audioLevel = useAudioLevel(stream);
  const { devices, selectedDeviceId, setSelectedDeviceId } = useMediaDevices();

  const permissionDenied = useMemo(
    () => state.permissionStatus === MicrophonePermissionStatus.Denied,
    [state.permissionStatus],
  );

  // Re-acquire the stream when the user picks a different device.
  useEffect(() => {
    if (
      selectedDeviceId &&
      state.phase === VoiceRecordingState.PrePermission &&
      state.permissionStatus === MicrophonePermissionStatus.Prompt
    ) {
      return;
    }

    if (
      selectedDeviceId &&
      (state.phase === VoiceRecordingState.PrePermission ||
        state.phase === VoiceRecordingState.ReadyToRecord)
    ) {
      requestPermissions(selectedDeviceId);
    }
    // Intentionally only depends on selectedDeviceId — re-acquire on device change only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDeviceId]);

  const handleStartRecording = useCallback(async () => {
    try {
      await startRecording();
      dispatch({ type: VoiceAction.StartRecording });
    } catch {
      notifications.show({
        message: translate(
          "Unable to start recording. Please check your microphone permissions.",
        ),
        color: "red",
      });
    }
  }, [startRecording, translate]);

  const handleStopRecording = useCallback(async () => {
    try {
      const audioRecording = await stopRecording();
      dispatch({
        type: VoiceAction.RecordingReady,
        payload: audioRecording,
      });
    } catch (error) {
      dispatch({ type: VoiceAction.RecordingFailed });
      notifications.show({
        message:
          error instanceof Error
            ? error.message
            : translate("There was a problem recording your audio"),
        color: "red",
      });
    }
  }, [stopRecording, translate]);

  // Auto-stop at 0s remaining (no auto-submit so the candidate can still review/discard).
  useEffect(() => {
    if (
      duration >= maxDurationSeconds &&
      isRecording &&
      state.phase === VoiceRecordingState.ActiveRecording
    ) {
      handleStopRecording();
    }
  }, [
    duration,
    handleStopRecording,
    isRecording,
    maxDurationSeconds,
    state.phase,
  ]);

  const handleDeleteRecording = useCallback(() => {
    dispatch({ type: VoiceAction.DeleteRecording });
    reset();
  }, [reset]);

  const handleSwitchToText = useCallback(() => {
    onSwitchToText?.();
  }, [onSwitchToText]);

  const handleUploadStart = useCallback(() => {
    dispatch({ type: VoiceAction.SubmitRecording });
    onUploadingChange?.(true);
  }, [onUploadingChange]);
  const handleUploadSuccess = useCallback(() => {
    dispatch({ type: VoiceAction.SubmitSuccess });
    onUploadingChange?.(false);
  }, [onUploadingChange]);
  const handleUploadError = useCallback(() => {
    dispatch({ type: VoiceAction.SubmitFailed });
    onUploadingChange?.(false);
  }, [onUploadingChange]);

  // Text fallback toggle, shown whenever the parent allows it (voice_recommended
  // questions). Rendered in both PrePermission and ReadyToRecord so a candidate
  // who declines/can't grant microphone access is never blocked (AS-1911).
  const switchToTextButton = onSwitchToText ? (
    <Button
      className={styles.modeToggleButton}
      leftSection={<IconKeyboard size={16} />}
      onClick={handleSwitchToText}
      variant="subtle"
    >
      {translate("Type instead")}
    </Button>
  ) : null;

  // --- Render by state ---

  if (state.phase === VoiceRecordingState.Submitted) {
    return null;
  }

  if (state.phase === VoiceRecordingState.RecordingComplete) {
    if (!state.recordingOutput) {
      return null;
    }
    return (
      <Playback
        canSkip={canSkip}
        onSubmit={onSubmit}
        onUploadError={handleUploadError}
        onUploadStart={handleUploadStart}
        onUploadSuccess={handleUploadSuccess}
        recording={state.recordingOutput}
        reset={handleDeleteRecording}
        translate={translate}
        voiceUrls={voiceUrls}
      />
    );
  }

  if (state.phase === VoiceRecordingState.PrePermission) {
    return (
      <Flex className={styles.wrapper}>
        <Flex align="center" className={styles.prePermissionRow} gap="sm">
          <button
            className={styles.recordingButton}
            disabled={permissionDenied}
            onClick={() => requestPermissions(selectedDeviceId)}
            type="button"
          >
            <IconMicrophone className={styles.microphoneIcon} size={18} />
            {permissionDenied
              ? translate("Mic access denied")
              : translate("Allow microphone access")}
          </button>
          <DeviceSelector
            devices={devices}
            disabled={permissionDenied}
            onSelect={setSelectedDeviceId}
            selectedDeviceId={selectedDeviceId}
          />
          <AudioLevelIndicator level={audioLevel} />
          {switchToTextButton}
        </Flex>
      </Flex>
    );
  }

  if (state.phase === VoiceRecordingState.ActiveRecording) {
    return (
      <Flex className={styles.wrapper}>
        <button
          className={styles.recordingButton}
          data-recording="true"
          onClick={handleStopRecording}
          type="button"
        >
          <IconMicrophone
            className={styles.microphoneIcon}
            data-recording="true"
            size={18}
          />
          {translate("Stop recording")}
        </button>
        <Text
          className={styles.countdown}
          data-testid={VOICE_TEST_IDS.countdown}
        >
          {formatCountdown(Math.max(maxDurationSeconds - duration, 0))}
        </Text>
      </Flex>
    );
  }

  // ReadyToRecord
  return (
    <Flex className={styles.wrapper}>
      <Flex align="center" gap="sm">
        <button
          className={styles.recordingButton}
          data-recording="false"
          onClick={handleStartRecording}
          type="button"
        >
          <IconMicrophone size={18} />
          {translate("Start recording")}
        </button>
        <DeviceSelector
          devices={devices}
          onSelect={setSelectedDeviceId}
          selectedDeviceId={selectedDeviceId}
        />
        <AudioLevelIndicator level={audioLevel} />
      </Flex>
      <Flex align="center" gap="sm">
        <Text
          className={styles.countdown}
          data-testid={VOICE_TEST_IDS.countdown}
        >
          {formatCountdown(maxDurationSeconds)}
        </Text>
        {switchToTextButton}
      </Flex>
    </Flex>
  );
};
